import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, CheckCircle, Save } from 'lucide-react';

const COLORS = {
  navyBlue: '#003366',
  orange: '#FF6B35',
  mustard: '#FFAA00',
  green: '#16A34A',
  lightGray: '#F9FAFB',
  white: '#FFFFFF',
  borderGray: '#D1D5DB',
  textGray: '#6B7280',
  textDark: '#1F2937',
};

const fetchWithTimeout = (url, options = {}, timeoutMs = 30000) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const token = window.sessionStorage.getItem('nwaAccessToken');
  const headers = { ...(options.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;
  return fetch(url, { ...options, headers, signal: controller.signal }).finally(() => clearTimeout(timer));
};

const todayDDMMYYYY = () => {
  const d = new Date();
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
};

const styles = {
  container: { minHeight: '100vh', backgroundColor: COLORS.lightGray },
  header: { backgroundColor: COLORS.navyBlue, color: COLORS.white, padding: '8px 16px', borderBottom: `2px solid ${COLORS.mustard}` },
  headerContent: { maxWidth: '1040px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  backButton: { backgroundColor: 'transparent', border: 'none', color: COLORS.white, display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', padding: '8px 0' },
  body: { maxWidth: '1040px', margin: '0 auto', padding: '24px' },
  card: { backgroundColor: COLORS.white, borderRadius: '8px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', padding: '24px', marginBottom: '20px' },
  label: { display: 'block', fontSize: '14px', fontWeight: '600', color: COLORS.textDark, marginBottom: '6px' },
  input: { width: '100%', padding: '10px 14px', border: `2px solid ${COLORS.borderGray}`, borderRadius: '6px', fontSize: '15px', boxSizing: 'border-box' },
  select: { width: '100%', padding: '10px 14px', border: `2px solid ${COLORS.borderGray}`, borderRadius: '6px', fontSize: '15px', backgroundColor: COLORS.white, boxSizing: 'border-box' },
  button: { backgroundColor: COLORS.orange, color: COLORS.white, padding: '12px 24px', borderRadius: '6px', fontWeight: '600', border: 'none', cursor: 'pointer', fontSize: '16px' },
  errorBox: { backgroundColor: '#FEE2E2', border: '1px solid #FCA5A5', color: '#991B1B', padding: '12px 16px', borderRadius: '6px', marginBottom: '16px' },
};

const SCALE_ORDER = ['emotional', 'conduct', 'hyperactivity', 'peer', 'prosocial'];

export default function SDQScreening({ student, apiUrl, facilitatorId, facilitatorName, schoolId, schoolName, programmeId, programmeName, initialTimepoint, onBack, onSaved }) {
  const [config, setConfig] = useState(null);
  const [responses, setResponses] = useState({});
  const [respondent, setRespondent] = useState('Parent');
  const [screeningDate, setScreeningDate] = useState(todayDDMMYYYY());
  const [administrationMode, setAdministrationMode] = useState('Individual');
  const administrationBatchId = ''; // kept blank internally; no practitioner entry needed
  const [assessorName, setAssessorName] = useState(facilitatorName || '');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [stage, setStage] = useState('form');
  const timepoint = initialTimepoint || 'Baseline';

  useEffect(() => {
    let active = true;
    fetchWithTimeout(`${apiUrl}/api/sdq/config`)
      .then((r) => r.json().then((data) => ({ ok: r.ok, data })))
      .then(({ ok, data }) => {
        if (!active) return;
        if (!ok) throw new Error(data.error || 'Could not load social-emotional profile configuration');
        setConfig(data);
      })
      .catch((e) => {
        if (active) setError(e.message || 'Could not load social-emotional profile configuration');
      });
    return () => { active = false; };
  }, [apiUrl]);

  const answeredByScale = useMemo(() => {
    const counts = { emotional: 0, conduct: 0, hyperactivity: 0, peer: 0, prosocial: 0 };
    if (!config?.items) return counts;
    config.items.forEach((item) => {
      if (responses[String(item.number)]) counts[item.scale] += 1;
    });
    return counts;
  }, [config, responses]);

  const minimum = config?.minimum_completed_items_per_scale || 3;
  const readyToSave = config && SCALE_ORDER.every((scale) => answeredByScale[scale] >= minimum);
  const answeredCount = Object.values(responses).filter(Boolean).length;

  const setAnswer = (number, value) => {
    setResponses((prev) => ({ ...prev, [String(number)]: value }));
  };

  const submit = async () => {
    if (!readyToSave) {
      setError(`Please complete at least ${minimum} items in each scale. Complete all 25 wherever possible.`);
      return;
    }
    setError('');
    setLoading(true);
    try {
      const res = await fetchWithTimeout(`${apiUrl}/api/sdq/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          student_code: student.student_code,
          screening_date: screeningDate,
          timepoint,
          respondent,
          responses,
          school_id: schoolId || student?.school_id || '',
          school_name: schoolName || '',
          programme_id: programmeId || student?.programme_id || '',
          programme_name: programmeName || '',
          assessor_name: assessorName,
          administration_mode: administrationMode,
          administration_batch_id: administrationBatchId,
          entered_by_user_id: facilitatorId,
          entered_by_name: facilitatorName,
          notes,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not save this social-emotional profile');
      setResult(data);
      setStage('complete');
      if (onSaved) onSaved(data);
    } catch (e) {
      setError(e.message || 'Could not save this social-emotional profile');
    } finally {
      setLoading(false);
    }
  };

  const Header = ({ title }) => (
    <div style={styles.header}>
      <div style={styles.headerContent}>
        <button style={styles.backButton} onClick={onBack}><ArrowLeft size={18} /> Back to {student.full_name}</button>
        <span style={{ fontSize: '13px', color: COLORS.mustard, fontWeight: '600' }}>{title}</span>
      </div>
    </div>
  );

  if (stage === 'complete') {
    const scoring = result?.scoring;
    return (
      <div style={styles.container}>
        <Header title="Social-Emotional Profiling: Saved" />
        <div style={styles.body}>
          <div style={styles.card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: COLORS.green, marginBottom: '16px' }}>
              <CheckCircle size={28} />
              <h2 style={{ fontSize: '20px', fontWeight: 'bold', margin: 0 }}>Saved</h2>
            </div>
            {scoring && (
              <>
                <div style={{ backgroundColor: '#F8FAFC', border: `1px solid ${COLORS.borderGray}`, borderRadius: '8px', padding: '16px', margin: '16px 0' }}>
                  <div style={{ fontSize: '14px', color: COLORS.textGray }}>Total difficulties</div>
                  <div style={{ fontSize: '26px', fontWeight: '700', color: COLORS.navyBlue }}>{scoring.total_difficulties} <span style={{ fontSize: '16px' }}>({scoring.total_difficulties_band})</span></div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '10px', marginBottom: '18px' }}>
                  {SCALE_ORDER.map((scale) => (
                    <div key={scale} style={{ border: `1px solid ${COLORS.borderGray}`, borderRadius: '8px', padding: '12px' }}>
                      <div style={{ fontSize: '12px', color: COLORS.textGray }}>{config?.scale_labels?.[scale] || scale}</div>
                      <div style={{ fontSize: '18px', fontWeight: '700', color: COLORS.textDark }}>{scoring.scales[scale].score}</div>
                      <div style={{ fontSize: '13px', color: COLORS.textGray }}>{scoring.scales[scale].band}{scoring.scales[scale].prorated ? ' · prorated' : ''}</div>
                    </div>
                  ))}
                </div>
              </>
            )}
            <button style={{ ...styles.button, backgroundColor: COLORS.white, color: COLORS.navyBlue, border: `2px solid ${COLORS.navyBlue}` }} onClick={onBack}>Back to {student.full_name}</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <Header title="Social-Emotional Profiling" />
      <div style={styles.body}>
        <div style={styles.card}>
          <h1 style={{ fontSize: '22px', fontWeight: 'bold', color: COLORS.navyBlue, margin: '0 0 6px' }}>Strengths and Difficulties Questionnaire - {student.full_name}</h1>
          <p style={{ color: COLORS.textGray, margin: '0 0 16px' }}>P4-17 without impact · Timepoint: {timepoint}</p>
          {config?.instructions && <p style={{ fontSize: '14px', lineHeight: 1.5, color: COLORS.textDark, margin: '0 0 18px' }}>{config.instructions}</p>}
          {error && <div style={styles.errorBox}>{error}</div>}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '14px' }}>
            <div>
              <label style={styles.label}>Screening date (DD/MM/YYYY)</label>
              <input style={styles.input} value={screeningDate} onChange={(e) => setScreeningDate(e.target.value)} />
            </div>
            <div>
              <label style={styles.label}>Completed by</label>
              <select style={styles.select} value={respondent} onChange={(e) => setRespondent(e.target.value)}>
                <option value="Parent">Parent</option>
                <option value="Teacher">Teacher</option>
              </select>
            </div>
            <div>
              <label style={styles.label}>Administration mode</label>
              <select style={styles.select} value={administrationMode} onChange={(e) => setAdministrationMode(e.target.value)}>
                <option value="Individual">Individual</option>
                <option value="Group">Group</option>
              </select>
            </div>
            <div>
              <label style={styles.label}>Assessor name</label>
              <input style={styles.input} value={assessorName} onChange={(e) => setAssessorName(e.target.value)} />
            </div>
          </div>
        </div>

        <div style={styles.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '16px', flexWrap: 'wrap', marginBottom: '14px' }}>
            <h2 style={{ color: COLORS.navyBlue, fontSize: '18px', margin: 0 }}>Profile items</h2>
            <span style={{ fontSize: '13px', color: COLORS.textGray }}>{answeredCount}/25 answered</span>
          </div>

          {!config ? (
            <p style={{ color: COLORS.textGray }}>Loading profile items...</p>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <div style={{ minWidth: '760px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '40px minmax(330px, 1fr) repeat(3, 135px)', gap: '0', padding: '0 8px 8px', color: COLORS.textGray, fontSize: '12px', fontWeight: '600' }}>
                    <div>#</div><div style={{ textAlign: 'left' }}>Statement</div>
                    {config.response_options.map((option) => <div key={option} style={{ textAlign: 'center' }}>{option}</div>)}
                  </div>
                  {config.items.map((item) => (
                    <div key={item.number} style={{ display: 'grid', gridTemplateColumns: '40px minmax(330px, 1fr) repeat(3, 135px)', alignItems: 'center', borderTop: '1px solid #E5E7EB', minHeight: '54px', padding: '0 8px' }}>
                      <div style={{ fontSize: '13px', color: COLORS.textGray }}>{item.number}</div>
                      <div style={{ fontSize: '14px', color: COLORS.textDark, paddingRight: '12px', textAlign: 'left' }}>{item.text}</div>
                      {config.response_options.map((option) => (
                        <label key={option} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer', minHeight: '48px' }}>
                          <input
                            type="radio"
                            name={`sdq-${item.number}`}
                            value={option}
                            checked={responses[String(item.number)] === option}
                            onChange={() => setAnswer(item.number, option)}
                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                          />
                        </label>
                      ))}
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(165px, 1fr))', gap: '8px', marginTop: '18px' }}>
                {SCALE_ORDER.map((scale) => {
                  const count = answeredByScale[scale];
                  const ready = count >= minimum;
                  return (
                    <div key={scale} style={{ padding: '10px 12px', borderRadius: '6px', backgroundColor: ready ? '#F0FDF4' : '#FFF7ED', border: `1px solid ${ready ? '#86EFAC' : '#FDBA74'}` }}>
                      <div style={{ fontSize: '12px', color: COLORS.textGray }}>{config.scale_labels?.[scale] || scale}</div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: COLORS.textDark }}>{count}/5 answered</div>
                    </div>
                  );
                })}
              </div>
              <p style={{ fontSize: '12px', color: COLORS.textGray, lineHeight: 1.5, marginBottom: 0 }}>
                Complete all items wherever possible. If only 3 or 4 items in a scale are available, the system applies the pro-rata rule from the supplied scoring sheet.
              </p>
            </>
          )}
        </div>

        <div style={styles.card}>
          <label style={styles.label}>Recommendations / notes (optional)</label>
          <textarea style={{ ...styles.input, minHeight: '80px', fontFamily: 'inherit', resize: 'vertical' }} value={notes} onChange={(e) => setNotes(e.target.value)} />
          <button style={{ ...styles.button, marginTop: '16px', display: 'flex', alignItems: 'center', gap: '8px', opacity: loading || !readyToSave ? 0.6 : 1 }} onClick={submit} disabled={loading || !readyToSave}>
            <Save size={18} /> {loading ? 'Saving...' : 'Submit & Lock'}
          </button>
        </div>
      </div>
    </div>
  );
}
